import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography, Skeleton, useTheme, alpha, Grid } from '@mui/material'
import { Layers } from 'lucide-react'
import { ResponsiveBar } from '@nivo/bar'
import { useTranslation } from 'react-i18next'
import { analyticsApi } from '../../../../api/analyticsApi'

const FeatureAdoptionTab = ({ period }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await analyticsApi.getFeatureUsage(period)
        setData(res.data)
      } catch (err) {
        console.error('Failed to fetch feature usage:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [period])

  const features = data?.features || data || []

  const barData = features.map((f) => ({
    feature: f.category || f.feature || f.name,
    count: f.total_count || f.count || 0,
  }))

  const chartTheme = {
    axis: {
      ticks: {
        text: { fill: theme.palette.text.secondary, fontSize: 11 },
      },
    },
    grid: {
      line: {
        stroke: alpha(theme.palette.divider, 0.25),
        strokeWidth: 1,
      },
    },
    tooltip: {
      container: {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: '8px',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 24px rgba(0, 0, 0, 0.6)'
          : '0 4px 16px rgba(0, 0, 0, 0.08)',
        border: `1px solid ${theme.palette.divider}`,
        fontSize: '0.78rem',
        padding: '8px 12px',
      },
    },
  }

  return (
    <Box
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {/* Feature Cards Grid */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {t('analytics_feature_usage_by_category')}
      </Typography>

      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
            </Grid>
          ))}
        </Grid>
      ) : features.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {t('analytics_no_feature_data')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {features.map((feature, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: alpha(goldColor, 0.4),
                    boxShadow: `0 4px 16px ${alpha(goldColor, 0.08)}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      background: alpha(goldColor, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: goldColor,
                    }}
                  >
                    <Layers size={18} strokeWidth={1.75} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      color: theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {feature.category || feature.feature || feature.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2 }}>
                      {(feature.total_count || feature.count || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}>
                      {t('analytics_total_uses')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2 }}>
                      {(feature.unique_users || feature.unique || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}>
                      {t('analytics_unique_users')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Horizontal Bar Chart */}
      {!loading && barData.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t('analytics_feature_usage_comparison')}
          </Typography>
          <Box sx={{ height: Math.max(barData.length * 40 + 60, 200) }}>
            <ResponsiveBar
              data={barData}
              keys={['count']}
              indexBy="feature"
              layout="horizontal"
              theme={chartTheme}
              margin={{ top: 10, right: 30, bottom: 40, left: 140 }}
              padding={0.3}
              colors={[goldColor]}
              borderRadius={4}
              enableGridX={true}
              enableGridY={false}
              axisBottom={{
                tickSize: 0,
                tickPadding: 8,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
              }}
              labelSkipWidth={40}
              labelTextColor={theme.palette.text.primary}
              tooltip={({ indexValue, value }) => (
                <Box
                  sx={{
                    background: theme.palette.background.paper,
                    p: '8px 12px',
                    borderRadius: '8px',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 24px rgba(0, 0, 0, 0.6)'
                      : '0 4px 16px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {indexValue}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {value.toLocaleString()} {t('analytics_uses')}
                  </Typography>
                </Box>
              )}
            />
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default FeatureAdoptionTab
